<template>
  <v-container fluid mb-4>
    <v-row justify="center">
      <v-col cols="12" lg="9">
        <v-card max-width="1200">
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ $t('Подача запроса по торговой процедуре ({0})', [ $t(requestType ? LOT_REQUEST_TYPE_ENUM[requestType] : '') ]) }}
            </v-toolbar-title>
          </v-toolbar>
          <general-error v-if="!!generalError" />
          <v-row v-if="!success" class="px-2">
            <v-col>
              <request-result-info
                  :request-type="requestType"
                  :process-comment="request.processComment"
                  :request-status="request.status"
                  :organization-id="request.organizationId" />
            </v-col>
          </v-row>
          <v-row v-if="success" class="px-2">
            <v-col>
              <v-alert
                  class="text-center"
                  type="success"
                  :value="success">
                {{ $t('Запрос успешно отправлен на обработку.') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row class="px-2">
            <v-col>
              <lot-info-view :lot="lot" attachments>
                <template v-slot:attachments>
                  <business-document-list
                      ref="requestFiles"
                      :request-files="requestFiles"
                      :document-types="requiredDocumentTypes"
                      :readonly="readonly"
                      :loading="isLoading"
                      :lot-request-info="lotRequestInfo"
                      @deleted="getLotRequestFiles"
                      @uploaded="attachFileToLotRequest" />
                </template>
              </lot-info-view>
            </v-col>
          </v-row>
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
          <v-card-actions>
            <v-spacer />
            <v-btn
                v-if="canSendRequest"
                id="sendBtn"
                color="primary"
                :disabled="readonly"
                @click="createLotRequest">
              {{ $t('Отправить') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import Vue from 'vue'
  import LotInfoView from '@/components/reused/lot/LotInfoView.vue'
  import BusinessDocumentList from '@/components/reused/files/BusinessDocumentList.vue'
  import RequestResultInfo from '@/components/reused/lot/RequestResultInfo.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { LOT_REQUEST_TYPE_ENUM } from '@/constants/enums'
  import { REQUEST_STATUSES, LOT_REQUEST_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { LOT_MANAGEMENT, LOT_REQUESTS_MANAGEMENT, BUSINESS_FILES } from '@/constants/store.js'

  export default {
    components: {
      BusinessDocumentList,
      LotInfoView,
      RequestResultInfo,
      GeneralError
    },
    props: {
      lotId: {
        type: String,
        required: true
      },
      requestType: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        LOT_REQUEST_TYPE_ENUM,
        isLoading: false,
        success: false
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        request: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.LAST] || {})
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        requestFiles: state => (state[BUSINESS_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[BUSINESS_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      lot () {
        return this.currentLot || {
          lotId: this.lotId,
          lotType: this.lotType
        }
      },
      lotRequestInfo () {
        return {
          lotId: this.lot.lotId,
          lotType: this.lot.lotType,
          requestType: this.requestType,
          creatorId: this.lot.creatorId
        }
      },
      readonly () {
        return !this.lot.lotId ||
          !this.canSendRequest ||
          this.isLoading
      },
      canSendRequest () {
        const status = this.request.status
        if (this.request.organizationId &&
          this.request.organizationId !== this.currentOrganizationId &&
          this.requestType === LOT_REQUEST_TYPES.CONTRACT_CONDITION_FAIL) {
          return true
        }
        if (status === REQUEST_STATUSES.INPROCESS ||
          (status === REQUEST_STATUSES.APPROVED &&
            this.requestType === LOT_REQUEST_TYPES.RECALL)) {
          return false
        }
        return true
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lot.lotId && vm.requestType) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        getLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LAST_LOT_REQUEST_INFO,
        createLotRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CREATE_LOT_REQUEST,
        clearLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CLEAR_LAST_LOT_REQUEST_INFO
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        getLotRequestFilesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_FILES,
        getLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES,
        attachFileToLotRequestAction: BUSINESS_FILES.ACTIONS.ATTACH_FILE_TO_LOT_REQUEST,
        clearLotRequestFilesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_FILES,
        clearLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearLastLotRequestInfoAction(),
            this.clearLotRequestRequiredDocumentTypesAction(),
            this.clearLotRequestFilesAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await Promise.all([
            this.getLotInfoAction(model),
            this.getLastLotRequestInfoAction(model),
            this.getLotRequestFiles()
          ])
          await this.getLotRequestRequiredDocumentTypesAction(model)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      attachFileToLotRequest: async function (file) {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await this.attachFileToLotRequestAction({
            ...model,
            fileId: file.fileId
          })
          await this.getLotRequestFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getLotRequestFiles: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await this.getLotRequestFilesAction(model)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      validateRequestFiles: async function () {
        try {
          await this.$refs.requestFiles.validate()
        } catch (error) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Для продолжения необходимо добавить документы.'))
        }
      },
      createLotRequest: async function () {
        try {
          this.isLoading = true
          this.success = false
          await this.createLotRequestAction({
            request: this.requestFiles,
            lotId: this.lot.lotId,
            requestType: this.requestType,
            lotType: this.lot.lotType
          })
          await this.getLastLotRequestInfoAction({
            lotId: this.lot.lotId,
            requestType: this.requestType,
            lotType: this.lot.lotType
          })
          await this.getLotRequestFiles()
          this.isLoading = false
          this.success = true
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
          // для запрета перехода на следующий шаг пробрасываем ошибку
        }
      },
      exitFromWizard: function () {
        this.goToRoute(`${this.urlRootPath}/lots/personal/${this.lot.lotStatus || ''}`)
      }
    }
  }
</script>
